import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getNavItems } from '.';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';
import { roles } from 'constants/constant';
import { getUserDetails } from 'auth/auth';
import fundmoreLogo from '/fundmoreai.png';
import { performLogout } from 'utils/logout';
import Modal from 'components/modal';
import Button from 'components/button';
import { isBulkUploadInProgress } from 'utils/bulkUploadTracker';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '../LanguageToggle/LanguageToggle';
import {
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
} from 'redux_store/services/notifications';
import { formatTimeDifferenceUTC } from 'constants/updatedAt';
import { EventEnums, NotificationTypeMap } from 'pages/notification/constant';
import styles from '../../styles/components/nav-bar.module.css';
import {
  LayoutGrid,
  FileText,
  BarChart3,
  Users,
  ClipboardList,
  User,
  FolderOpen,
  LogOut,
  Bell,
  CheckCircle2,
  X,
} from 'lucide-react';

/**
 * Renders a navigation bar component.
 *
 * @returns {JSX.Element} The rendered navigation bar component.
 */
const NavBar = () => {
  const { t, i18n } = useTranslation(['navigation', 'common']);
  const { userRole } = useSelector(userRoleData);
  const location = useLocation();
  const navigate = useNavigate();
  const { firstName, lastName, userEmail } = getUserDetails();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const showUnread = false;

  const navItems = useMemo(() => getNavItems(t, userRole), [t, i18n.language, userRole]);

  const filteredNavItems = useMemo(() => {
    return navItems?.filter((item) => {
      // Explicitly hide Reports for loan processors
      if (userRole === roles.loanProcessor && item.title === 'Reports') {
        return false;
      }
      return item?.role?.includes(userRole);
    });
  }, [navItems, userRole]);

  const iconMap = {
    Dashboard: LayoutGrid,
    Profile: User,
    Pipeline: FileText,
    Documents: FolderOpen,
    Tools: BarChart3,
    Admin: Users,
    'User Management': Users,
    Reports: ClipboardList,
  };

  const {
    currentData: notificationData,
    isFetching: notificationFetching,
  } = useGetNotificationsQuery(
    { showUnread },
    { pollingInterval: 60000 }
  );
  const [updateNotification] = useUpdateNotificationsMutation();
  const notificationList = notificationData?.data?.data ?? [];
  const unreadCount = notificationList.filter(
    (item) => item.isRead === '0'
  ).length;
  const avatarPalette = [
    styles.notificationAvatarPrimary,
    styles.notificationAvatarNeutral,
    styles.notificationAvatarAccent,
  ];

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((name) => name?.[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const displayName =
    `${firstName ?? ''} ${lastName ?? ''}`.trim() || t('common:unknownUser');
  const displayEmail = userEmail || t('common:noEmailOnFile');
  
  const handleLogout = () => {
    if (isBulkUploadInProgress()) {
      setShowLogoutConfirm(true);
      return;
    }
    performLogout();
  };

  const handleMarkAsRead = async (notificationId) => {
    await updateNotification({
      data: { notification_ids: [notificationId] },
    });
  };

  const handleNotificationClick = async (notification) => {
    const { typeId, applicationId, userNotificationId, isRead } = notification;
    if (isRead === '0') {
      await updateNotification({
        data: { notification_ids: [userNotificationId] },
      });
    }
    const eventType = NotificationTypeMap[typeId];
    if (eventType) {
      switch (eventType) {
        case EventEnums.CONSUMER_PROFILE_ID_CREATED:
          navigate('/profile');
          break;
        case EventEnums.CONSUMER_PROFILE_COMPLETED:
        case EventEnums.CONSUMER_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVED:
          navigate('/pipeline');
          break;
        case EventEnums.CONSUMER_NOT_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVAL_REJECTION:
          navigate('/profile');
          break;
        case EventEnums.LOAN_OFFICER_CONSUMER_COMPLETED_ONBOARDING:
        case EventEnums.LOAN_OFFICER_PROFILE_COMPLETED:
        case EventEnums.LOAN_OFFICER_ADD_LEAD:
        case EventEnums.LOAN_OFFICER_ELIGIBLE:
        case EventEnums.LOAN_OFFICER_NOT_ELIGIBLE:
          navigate(`/loanInformation/lead?applicationId=${applicationId}`);
          break;
        case EventEnums.LOAN_OFFICER_PRE_APPROVED:
        case EventEnums.LOAN_OFFICER_PRE_APPROVAL_REJECTION:
          navigate(`/pipeline/lead?applicationId=${applicationId}`);
          break;
        case EventEnums.CONSUMER_QUOTE:
        case EventEnums.CONSUMER_EMAIL_CHANGE_VERIFICATION:
        default:
          break;
      }
    }
    setShowNotifications(false);
  };

  const renderIcon = (item, isActive) => {
    if (React.isValidElement(item?.icon)) {
      return React.cloneElement(item.icon, {
        className: `${styles.navIcon} ${styles.navIconImage} ${
          isActive ? styles.navIconActive : styles.navIconInactive
        }`,
      });
    }

    const IconComponent = iconMap[item.title];
    if (IconComponent) {
      return (
        <IconComponent
          className={`${styles.navIcon} ${
            isActive ? styles.navIconActive : styles.navIconInactive
          }`}
        />
      );
    }

    return null;
  };

  const isPathActive = (path, subPaths = []) => {
    const currentPath = location.pathname;
    if (currentPath.endsWith(path)) {
      return true;
    }
    return subPaths?.some((subPath) => currentPath.startsWith(subPath));
  };

  return (
    <aside
      className={`${styles.sidebar} group ${showNotifications ? styles.sidebarNotificationOpen : ''}`}
    >
      <div>
        <div className={styles.logoRow}>
          <div className={styles.logoGroup}>
            <div className={styles.logoIcon}>
              <img
                src={fundmoreLogo}
                alt="fundmore.ai"
                className={styles.logoImage}
              />
            </div>
            <div className={styles.logoText}>
              <span>
                <span className={styles.logoLight}>fund</span>
                <span className={styles.logoBold}>more</span>
                <span className={styles.logoAccent}>.ai</span>
              </span>
            </div>
          </div>
        </div>

        <nav className={styles.navList}>
          {filteredNavItems?.map((item, index) => {
            const isActive = isPathActive(item.path, item.subPaths);
            const label = item.label ?? item.title;
            return (
              <Link
                key={`${item.title}-${index}`}
                to={item.path}
                className={`${styles.actionButton} ${
                  isActive ? styles.actionButtonActive : styles.actionButtonInactive
                }`}
              >
                <div className={styles.navIconWrapper}>
                  {renderIcon(item, isActive)}
                </div>
                <span
                  className={`${styles.navLabel} ${
                    isActive ? styles.navLabelActive : ''
                  }`}
                >
                  {label}
                </span>
                <div className={styles.tooltip}>
                  {label}
                  <div className={styles.tooltipArrow} />
                </div>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className={styles.footer}>
        <div className={styles.notificationWrapper}>
          {showNotifications && (
            <div
              className={`${styles.notificationPopup} ${styles.notificationPopupInline}`}
            >
              <div className={styles.notificationHeader}>
                <div className={styles.notificationHeaderTitle}>
                  <Bell
                    className={`${styles.navIcon} ${styles.notificationHeaderIcon}`}
                  />
                  <span>{t('navigation:notifications')}</span>
                </div>
                <div className={styles.notificationHeaderActions}>
                  <span className={styles.notificationCount}>
                    {t('common:newCount', { count: unreadCount })}
                  </span>
                  <button
                    type="button"
                    className={styles.notificationCloseButton}
                    onClick={() => setShowNotifications(false)}
                    aria-label={t('common:buttons.close', {
                      defaultValue: 'Close notifications',
                      keySeparator: false,
                    })}
                  >
                    <X className={styles.notificationCloseIcon} />
                  </button>
                </div>
              </div>
              <div className={styles.notificationList}>
                {notificationFetching && (
                  <div className={styles.notificationEmpty}>
                    {t('common:loadingNotifications')}
                  </div>
                )}
                {!notificationFetching && notificationList.length === 0 && (
                  <div className={styles.notificationEmpty}>
                    {t('common:noNewNotifications')}
                  </div>
                )}
                {!notificationFetching &&
                  notificationList.map((notification, index) => {
                    const isUnread = notification.isRead === '0';
                    const subject = notification.subject || t('common:notification');
                    const message = notification.message || '';
                    const timeLabel = notification?.createdAt
                      ? formatTimeDifferenceUTC(notification.createdAt)
                      : '';
                    const initialsFromSubject = subject
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((word) => word[0])
                      .join('')
                      .toUpperCase();
                    const avatarColor =
                      avatarPalette[index % avatarPalette.length];
                    return (
                      <div
                        key={notification.userNotificationId}
                        className={`${styles.notificationItem} ${
                          isUnread ? styles.notificationItemUnread : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div
                          className={`${styles.notificationAvatar} ${avatarColor}`}
                        >
                          <span>{initialsFromSubject || 'FM'}</span>
                        </div>
                        <div className={styles.notificationBody}>
                          <div className={styles.notificationMeta}>
                            <h4
                              className={
                                isUnread
                                  ? styles.notificationTitle
                                  : styles.notificationTitleRead
                              }
                            >
                              {subject}
                            </h4>
                            <span className={styles.notificationTime}>
                              {timeLabel}
                            </span>
                          </div>
                          <p className={styles.notificationMessage}>
                            {message}
                          </p>
                          {isUnread && (
                            <div className={styles.notificationActionRow}>
                              <button
                                className={styles.notificationAction}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleMarkAsRead(
                                    notification.userNotificationId
                                  );
                                }}
                              >
                                {t('common:buttons.markAsRead')}
                                <CheckCircle2
                                  className={styles.notificationActionIcon}
                                />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className={styles.notificationFooter}>
                <button className={styles.notificationFooterButton}>
                  {t('common:viewAllNotifications')}
                </button>
              </div>
            </div>
          )}

          {!showNotifications && (
            <button
              onClick={() => setShowNotifications(true)}
              className={`${styles.actionButton} ${styles.actionButtonInactive}`}
              aria-label={t('navigation:notifications')}
            >
              <div className={styles.navIconWrapper}>
                <Bell className={`${styles.navIcon} ${styles.navIconInactive}`} />
                {unreadCount > 0 && (
                  <span className={styles.notificationBadgeDot} />
                )}
              </div>
              <span className={styles.navLabel}>
                {t('navigation:notifications')}
              </span>
              <div className={styles.tooltip}>
                {t('navigation:notifications')}
                <div className={styles.tooltipArrow} />
              </div>
            </button>
          )}
        </div>

        <div className={styles.languageToggle}>
          <LanguageToggle variant="sidebar" forceExpanded={showNotifications} />
          <span className={styles.languageLabel}>
            {t('navigation:language')}
          </span>
        </div>

        <div className={styles.profileRow}>
          <div className={styles.profileAvatar}>{initials || 'SG'}</div>
          <div className={styles.profileText}>
            <p className={styles.profileName}>{displayName}</p>
            <p className={styles.profileEmail}>{displayEmail}</p>
          </div>
        </div>

        <button
          className={styles.signOutButton}
          onClick={handleLogout}
          aria-label={t('common:buttons.signOut')}
        >
          <LogOut className={styles.navIcon} />
          <span className={styles.navLabel}>
            {t('common:buttons.signOut')}
          </span>
          <div className={styles.tooltip}>
            {t('common:buttons.signOut')}
            <div className={styles.tooltipArrow} />
          </div>
        </button>
      </div>

      {showLogoutConfirm &&
        createPortal(
          <Modal
            show={showLogoutConfirm}
            width="max-w-[420px]"
            onClose={() => setShowLogoutConfirm(false)}
          >
          <div className="p-5">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('documents:bulkUpload.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {t(
                'documents:bulkUpload.signOutWarning',
                {
                  defaultValue:
                    'A document is still processing. Do you want to sign out anyway?',
                  keySeparator: false,
                }
              )}
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="tertiary"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('common:buttons.no', { defaultValue: 'No', keySeparator: false })}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowLogoutConfirm(false);
                  performLogout();
                }}
              >
                {t('common:buttons.yes', { defaultValue: 'Yes', keySeparator: false })}
              </Button>
            </div>
          </div>
          </Modal>,
          document.body
        )}
    </aside>
  );
};

export default NavBar;
